import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "../../components/Team/people.css";
import "./public-onboarding.css";
import usePublicPageScroll from "./usePublicPageScroll";
import { getDocumentRequestSelf, fulfilDocumentRequest } from "../../api/onboardingApi";

/* /docs/request/<token> — "your manager asked for a document": one upload from
   the phone, straight into the employee's file (the card's Docs tab). */

export default function DocumentRequestSelf() {
  usePublicPageScroll();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  /* Same rule as the onboarding link: a failure on a phone names its own cause. */
  const explain = (e) => {
    const status = e?.response?.status ?? 0;
    const detail = e?.response?.data?.detail || e?.response?.data?.error || '';
    if (!e?.response && /network|timeout/i.test(String(e?.message || ''))) return 'Your phone lost the connection before the upload finished. Check your signal and try again — nothing was saved.';
    if (status === 404) return 'This link isn’t valid any more. Ask your manager to send a new one.';
    if (status === 413) return 'That file is too large to upload. Take the photo again at a smaller size, or send it to your manager.';
    if (status === 502 || e?.response?.data?.error === 'storage_failed') return 'The file reached us but couldn’t be stored. Try again in a minute — nothing was saved.';
    if (status === 409) return detail || 'This request was cancelled.';
    return `${detail || 'Upload failed and nothing was saved.'}${status ? ` (error ${status})` : ''} Try again, or ask your manager.`;
  };

  const load = useCallback(async () => {
    try { const r = await getDocumentRequestSelf(token); setData(r.data); setError(null); return r.data; }
    catch (e) { setError(explain(e)); return null; }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  // Takes the file directly: it's called from onChange, before setFile has landed.
  const upload = async (picked) => {
    const f = picked || file;
    if (!f) return;
    setBusy(true);
    setError(null);
    try {
      await fulfilDocumentRequest(token, f);
      const fresh = await load();
      if (fresh && fresh.request?.status !== 'fulfilled') setError('That didn’t save. Try it once more, or ask your manager.');
      else setFile(null);
    } catch (e) { setError(explain(e)); }
    finally { setBusy(false); }
  };

  if (error && !data) return <div className="pub"><div className="pub-wrap"><div className="pub-err">{error}</div></div></div>;
  if (!data) return <div className="pub"><div className="pub-wrap"><div className="pub-sub">Loading…</div></div></div>;
  const r = data.request;
  const first = (data.employee?.name || "").split(" ")[0];
  const fulfilled = r.status === "fulfilled";

  return (
    <div className="pub">
      <div className="pub-wrap">
        <div>
          <div className="pub-biz">{data.tenant?.name || "Document request"}</div>
          <h1>{fulfilled ? "Received. Thank you." : `${first ? `${first}, we` : "We"} need your ${r.label}.`}</h1>
          <div className="pub-sub">{fulfilled ? `Your ${r.label} is on file.` : "Take a clear photo or pick the file. It goes straight to your employee file."}</div>
        </div>
        {r.note && !fulfilled && <div className="pub-note">{r.note}</div>}
        {error && <div className="pub-err">{error}</div>}
        {!fulfilled && (
          <section className="pub-card">
            <div className="pub-card-head"><span className="pub-card-title">{r.label}</span><span className="pub-todo">To do</span></div>
            {/* One tap: picking the file sends it. A separate Upload button is a
                step people skip — see the onboarding link, where two "completed"
                steps never made a single request. No `capture` either: it forces
                the camera and hides the file picker from anyone holding a PDF. */}
            <label className={`pub-btn ${busy ? 'secondary' : ''}`} aria-busy={busy}>
              {busy ? 'Uploading…' : file ? 'Choose a different file' : 'Take a photo / choose a file'}
              <input type="file" accept="image/*,.pdf,application/pdf" hidden disabled={busy} onChange={(e) => { const f = e.target.files?.[0] || null; e.target.value = ''; setFile(f); if (f && f.size <= 20 * 1048576) upload(f); }} />
            </label>
            {file && <div className="pub-file-name">{file.name} · {file.size >= 1048576 ? `${(file.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(file.size / 1024))} KB`}</div>}
            {file && file.size > 20 * 1048576 && <div className="pub-card-hint">That file is over 20 MB, so it wasn’t sent. Take the photo again at a smaller size, or pick a PDF.</div>}
          </section>
        )}
        {fulfilled && r.file && <section className="pub-card is-done"><div className="pub-card-head"><span className="pub-card-title">{r.file.name}</span><span className="pub-done">✓ On file</span></div></section>}
      </div>
    </div>
  );
}
